import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { useMerchantCurrency } from '@/hooks/useMerchantCurrency';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { logAudit } from '@/lib/coreAudit';
import { findResourceConflict, reserveResource } from '@/lib/coreConcurrency';
import { dispatchCoreAction } from '@/lib/coreActionBridge';
import { emitCoreActivity } from '@/lib/coreEventHooks';
import {
  nightsBetween,
  computeTotal,
  isAvailableClientSide,
  todayISO,
  slugifyCategory,
  type HebergementProperty,
  type HebergementUnit,
  type HebergementBlock,
  type HebergementReservation,
  type HebergementMedia,
  type HebergementAmenity,
  type HebergementCategory,
  type CategoryInput,
  type ReservationStatus,
  type PaymentStatus,
  type PropertyStatus,
} from '@/lib/hebergement';

export interface ReservationDraft {
  propertyId: number;
  unitId: number | null;
  customerId?: number | null;
  guestName: string;
  guestEmail?: string;
  guestPhone?: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  pricePerNight: number;
  discount?: number;
  fees?: number;
  source?: string;
  notes?: string;
}

interface OverviewStats {
  arrivalsToday: number;
  departuresToday: number;
  currentGuests: number;
  upcoming: number;
  occupiedUnits: number;
  availableUnits: number;
  revenue: number;
  pendingPayments: number;
  occupancyRate: number;
}

const emptyStats: OverviewStats = {
  arrivalsToday: 0, departuresToday: 0, currentGuests: 0, upcoming: 0,
  occupiedUnits: 0, availableUnits: 0, revenue: 0, pendingPayments: 0, occupancyRate: 0,
};

export function useHebergement() {
  const { user } = useAuth();
  const merchantId = getCommerceId(user);
  const currency = useMerchantCurrency();

  const [properties, setProperties] = useState<HebergementProperty[]>([]);
  const [units, setUnits] = useState<HebergementUnit[]>([]);
  const [blocks, setBlocks] = useState<HebergementBlock[]>([]);
  const [reservations, setReservations] = useState<HebergementReservation[]>([]);
  const [media, setMedia] = useState<HebergementMedia[]>([]);
  const [amenities, setAmenities] = useState<HebergementAmenity[]>([]);
  const [categories, setCategories] = useState<HebergementCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [propRes, unitRes, blockRes, resaRes, mediaRes, amenityRes, catRes] = await Promise.all([
        supabase.from('hebergement_properties').select('*').eq('merchant_id', merchantId).order('created_at', { ascending: false }),
        supabase.from('hebergement_units').select('*').eq('merchant_id', merchantId).order('name', { ascending: true }),
        supabase.from('hebergement_blocks').select('*').eq('merchant_id', merchantId).order('start_date', { ascending: true }),
        supabase.from('hebergement_reservations').select('*').eq('merchant_id', merchantId).order('created_at', { ascending: false }),
        supabase.from('hebergement_media').select('*').eq('merchant_id', merchantId).order('sort_order', { ascending: true }),
        supabase.from('hebergement_amenities').select('*').eq('merchant_id', merchantId).order('name', { ascending: true }),
        supabase.from('typehebergement').select('*').order('sort_order', { ascending: true }),
      ]);
      if (propRes.error) throw propRes.error;
      if (unitRes.error) throw unitRes.error;
      if (blockRes.error) throw blockRes.error;
      if (resaRes.error) throw resaRes.error;
      if (mediaRes.error) throw mediaRes.error;
      if (amenityRes.error) throw amenityRes.error;

      // Les biens mis en avant (étoile) remontent toujours en tête de liste.
      const sortedProperties = ((propRes.data || []) as HebergementProperty[]).slice().sort((a, b) => {
        if (Boolean(b.featured) !== Boolean(a.featured)) return Number(Boolean(b.featured)) - Number(Boolean(a.featured));
        return String(b.created_at || '').localeCompare(String(a.created_at || ''));
      });
      setProperties(sortedProperties);
      setUnits((unitRes.data || []) as HebergementUnit[]);
      setBlocks((blockRes.data || []) as HebergementBlock[]);
      setReservations((resaRes.data || []) as HebergementReservation[]);
      setMedia((mediaRes.data || []) as HebergementMedia[]);
      setAmenities((amenityRes.data || []) as HebergementAmenity[]);
      // Types d'hébergement : globaux (idcommerce null) + ceux du marchand.
      setCategories(
        ((catRes.data || []) as HebergementCategory[]).filter(
          (c) => c.idcommerce == null || Number(c.idcommerce) === Number(merchantId),
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement du module hébergement');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  const unitsFor = useCallback(
    (propertyId: number) => units.filter((u) => u.property_id === propertyId),
    [units],
  );

  const propertyName = useCallback(
    (id: number | null) => properties.find((p) => p.id === id)?.name || '—',
    [properties],
  );

  const unitName = useCallback(
    (id: number | null) => (id == null ? 'Bien entier' : units.find((u) => u.id === id)?.name || `Unité #${id}`),
    [units],
  );

  // ── Propriétés ─────────────────────────────────────────────
  const createProperty = useCallback(async (data: Partial<HebergementProperty>): Promise<HebergementProperty> => {
    if (!merchantId) throw new Error('Non connecté');
    const { data: inserted, error: insErr } = await supabase
      .from('hebergement_properties')
      .insert({
        merchant_id: merchantId,
        owner_id: user?.id ?? null,
        name: data.name || 'Nouveau bien',
        type: data.type || 'apartment',
        description: data.description || null,
        address: data.address || null,
        city: data.city || null,
        region: data.region || null,
        country: data.country || null,
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
        capacity: data.capacity ?? 2,
        bed_count: data.bed_count ?? 1,
        bathroom_count: data.bathroom_count ?? 1,
        price: data.price ?? 0,
        currency,
        booking_mode: data.booking_mode || 'ENTIRE_PROPERTY',
        status: data.status || 'draft',
        published: data.published ?? false,
        cover_image: data.cover_image || null,
      })
      .select('*')
      .single();
    if (insErr) throw insErr;
    const property = inserted as HebergementProperty;
    setProperties((prev) => [property, ...prev]);
    return property;
  }, [merchantId, user, currency]);

  const updateProperty = useCallback(async (id: number, patch: Partial<HebergementProperty>) => {
    const { error: updErr } = await supabase
      .from('hebergement_properties')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (updErr) throw updErr;
    setProperties((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  /** Met en avant (ou retire) un bien — piloté par l'étoile de la carte. */
  const toggleFeatured = useCallback(async (id: number, featured: boolean) => {
    const { error: updErr } = await supabase
      .from('hebergement_properties')
      .update({ featured, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (updErr) throw updErr;
    setProperties((prev) => {
      const next = prev.map((p) => (p.id === id ? { ...p, featured } : p));
      return next.slice().sort((a, b) => {
        if (Boolean(b.featured) !== Boolean(a.featured)) return Number(Boolean(b.featured)) - Number(Boolean(a.featured));
        return String(b.created_at || '').localeCompare(String(a.created_at || ''));
      });
    });
  }, []);

  const deleteProperty = useCallback(async (id: number) => {
    const { error: delErr } = await supabase.from('hebergement_properties').delete().eq('id', id);
    if (delErr) throw delErr;
    await supabase.from('hebergement_units').delete().eq('property_id', id);
    setProperties((prev) => prev.filter((p) => p.id !== id));
    setUnits((prev) => prev.filter((u) => u.property_id !== id));
  }, []);

  // ── Unités ─────────────────────────────────────────────────
  const createUnit = useCallback(async (data: Partial<HebergementUnit>): Promise<HebergementUnit> => {
    if (!merchantId) throw new Error('Non connecté');
    const { data: inserted, error: insErr } = await supabase
      .from('hebergement_units')
      .insert({
        merchant_id: merchantId,
        property_id: data.property_id,
        name: data.name || 'Unité',
        type: data.type || 'room',
        description: data.description || null,
        capacity: data.capacity ?? 2,
        beds: data.beds ?? 1,
        bathrooms: data.bathrooms ?? 1,
        price: data.price ?? 0,
        status: data.status || 'active',
        published: data.published ?? false,
      })
      .select('*')
      .single();
    if (insErr) throw insErr;
    const unit = inserted as HebergementUnit;
    setUnits((prev) => [...prev, unit]);
    return unit;
  }, [merchantId]);

  const updateUnit = useCallback(async (id: number, patch: Partial<HebergementUnit>) => {
    const { error: updErr } = await supabase
      .from('hebergement_units')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (updErr) throw updErr;
    setUnits((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }, []);

  const deleteUnit = useCallback(async (id: number) => {
    const { error: delErr } = await supabase.from('hebergement_units').delete().eq('id', id);
    if (delErr) throw delErr;
    setUnits((prev) => prev.filter((u) => u.id !== id));
  }, []);

  // ── Blocages ───────────────────────────────────────────────
  const createBlock = useCallback(async (data: { propertyId: number; unitId: number | null; startDate: string; endDate: string; reason?: string }) => {
    if (!merchantId) throw new Error('Non connecté');
    const { data: inserted, error: insErr } = await supabase
      .from('hebergement_blocks')
      .insert({
        merchant_id: merchantId,
        property_id: data.propertyId,
        unit_id: data.unitId,
        start_date: data.startDate,
        end_date: data.endDate,
        reason: data.reason || null,
        created_by: user?.id ?? null,
      })
      .select('*')
      .single();
    if (insErr) throw insErr;
    setBlocks((prev) => [...prev, inserted as HebergementBlock]);
  }, [merchantId, user]);

  const deleteBlock = useCallback(async (id: number) => {
    const { error: delErr } = await supabase.from('hebergement_blocks').delete().eq('id', id);
    if (delErr) throw delErr;
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  }, []);

  // ── Disponibilité ──────────────────────────────────────────
  const checkAvailability = useCallback(async (
    propertyId: number,
    unitId: number | null,
    checkIn: string,
    checkOut: string,
  ): Promise<boolean> => {
    // Aperçu instantané côté client, puis confirmation serveur.
    const quick = isAvailableClientSide(propertyId, unitId, checkIn, checkOut, reservations, blocks);
    if (!quick) return false;
    try {
      const { data, error: rpcErr } = await supabase.rpc('hebergement_check_availability', {
        p_property_id: propertyId,
        p_unit_id: unitId,
        p_check_in: checkIn,
        p_check_out: checkOut,
      });
      if (rpcErr) throw rpcErr;
      return data === true;
    } catch {
      // En cas d'erreur réseau on garde l'aperçu client.
      return quick;
    }
  }, [reservations, blocks]);

  // ── Médias (galerie) ───────────────────────────────────────
  const mediaFor = useCallback(
    (propertyId: number) => media.filter((m) => m.property_id === propertyId),
    [media],
  );

  const amenitiesFor = useCallback(
    (propertyId: number) => amenities.filter((a) => a.property_id === propertyId),
    [amenities],
  );

  /** Remplace les médias d'un bien (les unités non gérées ici). */
  const savePropertyMedia = useCallback(async (
    propertyId: number,
    items: { url: string; type: string; caption?: string }[],
  ) => {
    if (!merchantId) throw new Error('Non connecté');
    // Supprime les anciens médias du bien puis réinsère la liste ordonnée.
    const { error: delErr } = await supabase
      .from('hebergement_media')
      .delete()
      .eq('property_id', propertyId)
      .is('unit_id', null);
    if (delErr) throw delErr;
    if (items.length > 0) {
      const rows = items.map((it, i) => ({
        merchant_id: merchantId,
        property_id: propertyId,
        unit_id: null,
        url: it.url,
        type: it.type || 'image',
        caption: it.caption || null,
        sort_order: i,
        is_cover: i === 0,
      }));
      const { error: insErr } = await supabase.from('hebergement_media').insert(rows);
      if (insErr) throw insErr;
    }
    await load();
  }, [merchantId, load]);

  const savePropertyAmenities = useCallback(async (propertyId: number, names: string[]) => {
    if (!merchantId) throw new Error('Non connecté');
    const { error: delErr } = await supabase
      .from('hebergement_amenities')
      .delete()
      .eq('property_id', propertyId)
      .is('unit_id', null);
    if (delErr) throw delErr;
    if (names.length > 0) {
      const rows = names.map((name) => ({
        merchant_id: merchantId,
        property_id: propertyId,
        unit_id: null,
        name,
      }));
      const { error: insErr } = await supabase.from('hebergement_amenities').insert(rows);
      if (insErr) throw insErr;
    }
    await load();
  }, [merchantId, load]);

  // ── Types d'hébergement (catégories + sous-types personnalisables) ──
  const createCategory = useCallback(async (input: CategoryInput): Promise<HebergementCategory | null> => {
    if (!merchantId) throw new Error('Non connecté');
    const clean = (input?.nom || '').trim();
    if (!clean) return null;
    const slug = `c-${slugifyCategory(clean)}-${Math.random().toString(36).slice(2, 6)}`;
    const maxOrder = categories.reduce((m, c) => Math.max(m, Number(c.sort_order) || 0), 100);
    const { data, error: insErr } = await supabase
      .from('typehebergement')
      .insert({
        idcommerce: merchantId,
        nom: clean,
        slug,
        icon: input?.icon || 'ri-building-4-line',
        image_couverture: input?.image_couverture || null,
        parent_id: input?.parent_id || 0,
        sort_order: maxOrder + 1,
      })
      .select('*')
      .single();
    if (insErr) throw insErr;
    const created = data as HebergementCategory;
    setCategories((prev) => [...prev, created]);
    return created;
  }, [merchantId, categories]);

  /** Met à jour un type d'hébergement — y compris les types par défaut (nom, icône, image, parent). */
  const updateCategory = useCallback(async (id: number, patch: Partial<CategoryInput>) => {
    const target = categories.find((c) => c.id === id);
    if (!target) return;
    const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (patch.nom !== undefined) row.nom = patch.nom.trim();
    if (patch.icon !== undefined) row.icon = patch.icon;
    if (patch.image_couverture !== undefined) row.image_couverture = patch.image_couverture;
    if (patch.parent_id !== undefined) row.parent_id = patch.parent_id || 0;
    const { error: updErr } = await supabase.from('typehebergement').update(row).eq('id', id);
    if (updErr) throw updErr;
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } as HebergementCategory : c)));
  }, [categories]);

  /** Supprime un type personnalisé du marchand (jamais un type global). */
  const deleteCategory = useCallback(async (id: number) => {
    const target = categories.find((c) => c.id === id);
    if (!target || target.idcommerce == null) return;
    const { error: delErr } = await supabase.from('typehebergement').delete().eq('id', id);
    if (delErr) throw delErr;
    setCategories((prev) => prev.filter((c) => c.id !== id));
  }, [categories]);

  // ── Réservations ───────────────────────────────────────────
  const createReservation = useCallback(async (
    draft: ReservationDraft,
    opts?: { idempotencyKey?: string | null },
  ): Promise<number> => {
    if (!merchantId) throw new Error('Non connecté');

    // Organisation Core (concurrence + journalisation).
    let organizationId: number | null = null;
    try {
      const org = await getOrganizationByCommerce(merchantId);
      organizationId = org?.id ?? null;
    } catch {
      organizationId = null;
    }

    // ── Concurrence (spec 48) : une ressource exclusive ne peut pas être
    // réservée deux fois sur la même plage. Vérification AVANT l'action.
    const resourceType = draft.unitId != null ? 'room' : 'accommodation';
    const resourceKey = draft.unitId != null ? `unit-${draft.unitId}` : `property-${draft.propertyId}`;
    if (organizationId) {
      const conflict = await findResourceConflict({
        organizationId,
        resourceType,
        resourceId: resourceKey,
        startAt: draft.checkIn,
        endAt: draft.checkOut,
      });
      if (conflict) throw new Error('Ce bien (ou cette unité) est déjà réservé sur ces dates.');
    }

    // ── Action Layer (46/47) : création idempotente + journalisation.
    const actionResult = await dispatchCoreAction({
      commerceId: merchantId,
      actionKey: 'create_booking',
      entityType: 'reservation',
      resourceType,
      resourceId: resourceKey,
      permission: 'products.write',
      idempotencyKey: opts?.idempotencyKey ?? null,
      idempotencyScope: 'reservations',
      source: 'hebergement',
      actorUserId: user?.id ?? null,
      actorName: user?.name ?? null,
      parameters: {
        property_id: draft.propertyId,
        unit_id: draft.unitId,
        check_in: draft.checkIn,
        check_out: draft.checkOut,
        guests: draft.guests,
      },
      execute: async () => {
        const { data, error: rpcErr } = await supabase.rpc('hebergement_create_reservation', {
          p_merchant_id: merchantId,
          p_property_id: draft.propertyId,
          p_unit_id: draft.unitId,
          p_customer_id: draft.customerId ?? null,
          p_guest_name: draft.guestName,
          p_guest_email: draft.guestEmail || null,
          p_guest_phone: draft.guestPhone || null,
          p_check_in: draft.checkIn,
          p_check_out: draft.checkOut,
          p_guests: draft.guests,
          p_price_per_night: draft.pricePerNight,
          p_discount: draft.discount ?? 0,
          p_fees: draft.fees ?? 0,
          p_currency: currency,
          p_source: draft.source || 'manual',
          p_notes: draft.notes || null,
        });
        if (rpcErr) {
          if (/indisponible/i.test(rpcErr.message)) {
            throw new Error('Ce bien (ou cette unité) est déjà réservé sur ces dates.');
          }
          if (/dates_invalides/i.test(rpcErr.message)) {
            throw new Error('Dates invalides : la date de départ doit suivre la date d\u2019arrivée.');
          }
          throw rpcErr;
        }
        const resaId = data as number;

        // Pose le verrou de ressource (48, best-effort) reliant le verrou à la
        // réservation — base de la détection de double booking.
        if (organizationId) {
          try {
            await reserveResource({
              tenantId: null,
              organizationId,
              resourceType,
              resourceId: resourceKey,
              label: `Réservation #${resaId}`,
              holderUserId: user?.id ?? null,
              holderName: draft.guestName || null,
              startAt: draft.checkIn,
              endAt: draft.checkOut,
              entityType: 'reservation',
              entityId: resaId,
            });
          } catch {
            /* le verrou ne doit pas annuler une réservation déjà posée */
          }
        }

        // Événement du socle : Event Bus + Timeline + notifications + réindexation.
        await emitCoreActivity({
          commerceId: merchantId,
          eventKey: 'booking.created',
          entityType: 'reservation',
          entityId: resaId,
          title: `Réservation #${resaId} · ${draft.guestName}`,
          description: `${draft.checkIn} → ${draft.checkOut}`,
          amount: (draft.pricePerNight || 0) * nightsBetween(draft.checkIn, draft.checkOut),
          currency,
          payload: { guest: draft.guestName, property_id: draft.propertyId, unit_id: draft.unitId, source: draft.source || 'manual' },
          actorUserId: user?.id ?? null,
          actorName: user?.name ?? null,
          source: 'hebergement',
        });

        return { entityId: resaId, result: { reservation_id: resaId } };
      },
    });

    if (!actionResult.ok) {
      throw new Error(actionResult.error || 'Erreur lors de la réservation.');
    }

    const resaId = actionResult.entityId ? Number(actionResult.entityId) : 0;
    await load();
    return resaId;
  }, [merchantId, currency, load, user]);

  const updateReservationStatus = useCallback(async (id: number, status: ReservationStatus) => {
    const { error: updErr } = await supabase
      .from('hebergement_reservations')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (updErr) throw updErr;
    setReservations((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  }, []);

  const updateReservationPayment = useCallback(async (id: number, paymentStatus: PaymentStatus) => {
    const { error: updErr } = await supabase
      .from('hebergement_reservations')
      .update({ payment_status: paymentStatus, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (updErr) throw updErr;
    setReservations((prev) => prev.map((r) => (r.id === id ? { ...r, payment_status: paymentStatus } : r)));
  }, []);

  const deleteReservation = useCallback(async (id: number) => {
    const target = reservations.find((r) => r.id === id);
    const { error: delErr } = await supabase.from('hebergement_reservations').delete().eq('id', id);
    if (delErr) throw delErr;
    setReservations((prev) => prev.filter((r) => r.id !== id));
    // Journalise la suppression (auteur + horodatage) — best-effort.
    try {
      if (merchantId) {
        const org = await getOrganizationByCommerce(merchantId);
        if (org) {
          await logAudit({
            tenantId: org.tenant_id, organizationId: org.id,
            userId: user?.id ?? null, userName: user?.name ?? null,
            action: 'delete', resourceType: 'booking', resourceId: String(id),
            before: {
              guest: target?.guest_name ?? null,
              check_in: target?.check_in ?? null,
              check_out: target?.check_out ?? null,
              total: target ? Number(target.total) || 0 : null,
            },
          });
        }
      }
    } catch { /* best-effort */ }
  }, [reservations, merchantId, user]);

  // ── Statistiques ───────────────────────────────────────────
  const stats = useMemo<OverviewStats>(() => {
    if (properties.length === 0 && reservations.length === 0) return emptyStats;
    const today = todayISO();
    const activeResa = reservations.filter((r) => r.status !== 'cancelled');

    const arrivalsToday = activeResa.filter((r) => String(r.check_in).slice(0, 10) === today).length;
    const departuresToday = activeResa.filter((r) => String(r.check_out).slice(0, 10) === today).length;
    const currentGuests = activeResa
      .filter((r) => String(r.check_in).slice(0, 10) <= today && String(r.check_out).slice(0, 10) > today)
      .reduce((sum, r) => sum + (Number(r.guests) || 0), 0);
    const upcoming = activeResa.filter((r) => String(r.check_in).slice(0, 10) > today).length;

    const occupiedNow = activeResa.filter(
      (r) => String(r.check_in).slice(0, 10) <= today && String(r.check_out).slice(0, 10) > today,
    );
    const occupiedUnits = new Set(occupiedNow.map((r) => r.unit_id ?? `p-${r.property_id}`)).size;

    const bookableUnits = units.filter((u) => u.status === 'active').length
      + properties.filter((p) => p.booking_mode !== 'UNITS_ONLY').length;
    const availableUnits = Math.max(0, bookableUnits - occupiedUnits);

    const revenue = activeResa.reduce((sum, r) => sum + (Number(r.total) || 0), 0);
    const pendingPayments = activeResa
      .filter((r) => r.payment_status === 'unpaid' || r.payment_status === 'partial')
      .reduce((sum, r) => sum + (Number(r.total) || 0), 0);

    const occupancyRate = bookableUnits > 0 ? Math.round((occupiedUnits / bookableUnits) * 100) : 0;

    return { arrivalsToday, departuresToday, currentGuests, upcoming, occupiedUnits, availableUnits, revenue, pendingPayments, occupancyRate };
  }, [properties, units, reservations]);

  const upcomingReservations = useMemo(
    () => reservations
      .filter((r) => r.status !== 'cancelled' && String(r.check_in).slice(0, 10) >= todayISO())
      .slice()
      .sort((a, b) => String(a.check_in).localeCompare(String(b.check_in)))
      .slice(0, 6),
    [reservations],
  );

  return {
    merchantId,
    currency,
    properties,
    units,
    blocks,
    reservations,
    media,
    amenities,
    categories,
    loading,
    error,
    reload: load,
    stats,
    upcomingReservations,
    unitsFor,
    mediaFor,
    amenitiesFor,
    savePropertyMedia,
    savePropertyAmenities,
    createCategory,
    updateCategory,
    deleteCategory,
    propertyName,
    unitName,
    createProperty,
    updateProperty,
    toggleFeatured,
    deleteProperty,
    createUnit,
    updateUnit,
    deleteUnit,
    createBlock,
    deleteBlock,
    checkAvailability,
    createReservation,
    updateReservationStatus,
    updateReservationPayment,
    deleteReservation,
    nightsBetween,
    computeTotal,
  };
}